import { startTransition, useEffect, useRef } from 'react'

/**
 * URL search params as TanStack Router's search reducers read and write them. Values are not
 * checked here: the route's `validateSearch` parses the URL this hook writes.
 */
export type IndexTableSearch = Record<string, unknown>

type IndexTableNavigateOverrides = {
  replace?: boolean
  resetScroll?: boolean
}

/** The navigation this hook requests; `useNavigate()` accepts it. */
type IndexTableNavigateOptions = IndexTableNavigateOverrides & {
  // '.' is what an omitted `to` resolves to; naming it lets the router type the search.
  to: '.'
  search: (previous: IndexTableSearch) => IndexTableSearch
}

export type IndexTableSearchParamsOptions = {
  navigate: (options: IndexTableNavigateOptions) => unknown
  /** The page's search params, read from the current URL's. */
  getSearchParams: (previous: IndexTableSearch) => IndexTableSearch
  /** More updates to apply with a new search term (for example, a relevance sort). */
  getSearchTermUpdates?: (searchTerm: string) => IndexTableSearch
  searchTerm?: string
  resetPageOnSearch?: boolean
  resetScroll?: boolean
}

export type IndexTableSearchParamsHandlers = {
  updateSearchParams: (updates: IndexTableSearch, options?: IndexTableNavigateOverrides) => void
  updateSearchTerm: (value: string) => void
  updateSort: (sort: string) => void
  updatePage: (page: number) => void
  updatePageSize: (pageSize: number) => void
  updateTableParams: (updates: IndexTableSearch) => void
}

/**
 * Golden path for URL-backed IndexDataTable pages.
 *
 * Use this helper instead of hand-writing table navigation handlers. It keeps
 * search/filter/sort/page changes non-urgent, preserves the user's scroll
 * position for tables lower on the page, and resets pagination when a change
 * would otherwise leave the user on an empty later page.
 */
export function useIndexTableSearchParams({
  navigate,
  getSearchParams,
  getSearchTermUpdates,
  searchTerm = '',
  resetPageOnSearch = true,
  resetScroll = false,
}: IndexTableSearchParamsOptions): IndexTableSearchParamsHandlers {
  const latestSearchRef = useRef(searchTerm)

  // Keep filter/sort/page actions aligned with browser back/forward search changes.
  useEffect(() => {
    latestSearchRef.current = searchTerm
  }, [searchTerm])

  const updateSearchParams = (
    updates: IndexTableSearch,
    options: IndexTableNavigateOverrides = {}
  ) => {
    startTransition(() => {
      navigate({
        to: '.',
        search: (previous) => {
          const nextSearch: IndexTableSearch = {
            ...getSearchParams(previous),
            ...updates,
          }

          if (!Object.prototype.hasOwnProperty.call(updates, 'search')) {
            nextSearch.search = latestSearchRef.current.trim() || undefined
          }

          return nextSearch
        },
        replace: true,
        resetScroll,
        ...options,
      })
    })
  }

  const updateSearchTerm = (value: string) => {
    latestSearchRef.current = value.trim()
    updateSearchParams(
      {
        search: latestSearchRef.current || undefined,
        ...(typeof getSearchTermUpdates === 'function'
          ? getSearchTermUpdates(latestSearchRef.current)
          : {}),
        ...(resetPageOnSearch ? { page: 1 } : {}),
      },
      { resetScroll: false }
    )
  }

  const updateSort = (sort: string) => updateSearchParams({ sort, page: 1 })
  const updatePage = (page: number) => updateSearchParams({ page })
  const updatePageSize = (pageSize: number) => updateSearchParams({ page_size: pageSize, page: 1 })
  const updateTableParams = (updates: IndexTableSearch) => {
    if (Object.prototype.hasOwnProperty.call(updates, 'search')) {
      latestSearchRef.current = typeof updates.search === 'string' ? updates.search.trim() : ''
    }

    updateSearchParams({ ...updates, page: 1 })
  }

  return {
    updateSearchParams,
    updateSearchTerm,
    updateSort,
    updatePage,
    updatePageSize,
    updateTableParams,
  }
}
